import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, MessageCircle, Heart, User, Menu, X, ChevronDown, LogOut, Settings, Package, Globe, Shield, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { categoriesAPI } from '../../services/api';
import api from '../../services/api';
import { CATEGORIES } from '../../data/categories';
import SearchAutocomplete from '../search/SearchAutocomplete';
import { CategoryBrowser } from '../categories/CategoryBrowser';
import { CategoryMegaMenu } from '../categories/CategoryMegaMenu';

const Header = () => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage, languages, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(null); // null = loading, 0 = none
  const [savedSearchNew, setSavedSearchNew] = useState(0);

  useEffect(() => {
    if (!user) { setSavedSearchNew(0); return undefined; }
    const load = () => api.get('/saved-searches/new-count').then((r) => setSavedSearchNew(r.data?.total || 0)).catch(() => setSavedSearchNew(0));
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    window.addEventListener('saved-searches-changed', load);
    return () => { clearInterval(interval); window.removeEventListener('saved-searches-changed', load); };
  }, [user]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const categoryCounts = Object.fromEntries(categories.map((c) => [c.id, c.count]));

  // Track scroll for header shrinking on mobile
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await categoriesAPI.getAll();
        setCategories(cats.filter((c) => CATEGORIES.some((s) => s.id === c.id)));
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        try {
          const response = await api.get('/admin/check');
          setIsAdmin(response.data.is_admin);
        } catch (err) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [user]);

  // Fetch real unread message count - ONLY show badge when we KNOW there are unread messages
  useEffect(() => {
    // Reset to null (loading) immediately when user changes
    setUnreadMessages(null);
    
    const fetchUnreadCount = async () => {
      if (user) {
        try {
          const response = await api.get('/conversations/unread/count');
          // Only set if we get a valid response
          if (response.data && typeof response.data.unread_count === 'number') {
            setUnreadMessages(response.data.unread_count);
          } else {
            setUnreadMessages(0);
          }
        } catch (err) {
          console.error('Failed to fetch unread count:', err);
          // On error, set to 0 (no badge) rather than showing stale data
          setUnreadMessages(0);
        }
      } else {
        setUnreadMessages(0);
      }
    };
    
    if (user) {
      fetchUnreadCount();
    } else {
      setUnreadMessages(0);
    }
    
    // Expose refresh function globally so MessagesPage can trigger it
    window.refreshUnreadCount = fetchUnreadCount;
    
    // Refresh unread count every 30 seconds (reduced from 60 for better responsiveness)
    const interval = user ? setInterval(fetchUnreadCount, 30000) : null;
    
    // Also listen for custom event to refresh immediately
    const handleRefresh = () => fetchUnreadCount();
    window.addEventListener('refreshUnreadMessages', handleRefresh);
    
    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener('refreshUnreadMessages', handleRefresh);
      delete window.refreshUnreadCount;
    };
  }, [user]);

  // Close mobile menu when route changes using a callback in the Sheet
  const handleMobileMenuOpenChange = (open) => {
    setMobileMenuOpen(open);
  };

  // Helper function to close mobile menu and navigate
  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      {/* Top bar - hidden on mobile when scrolled */}
      <div className={`bg-gradient-to-r from-emerald-600 to-teal-500 text-white transition-all duration-300 ${
        isScrolled ? 'hidden md:block' : ''
      }`}>
        <div className="py-1.5 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="text-sm hidden sm:block">
              <span className="font-medium">{t('header.tagline').split('|')[0]}</span>
              <span className="mx-2">|</span>
              <span>{t('header.tagline').split('|')[1]}</span>
            </div>
            <div className="text-xs sm:hidden font-medium">LebonLux</div>
            
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-white hover:bg-white/20 hover:text-white gap-1.5"
                >
                  <span className="text-base">{currentLanguage.flag}</span>
                  <span className="text-xs font-medium">{currentLanguage.code.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`flex items-center gap-2 cursor-pointer ${language === lang.code ? 'bg-emerald-50 text-emerald-700' : ''}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.name}</span>
                    {language === lang.code && <span className="ml-auto text-emerald-600">✓</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Main header */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <h1 className={`font-bold transition-all duration-300 ${isScrolled ? 'text-xl' : 'text-xl sm:text-2xl'}`}>
              <span className="text-emerald-600">Lebon</span>
              <span className="text-teal-500">Lux</span>
            </h1>
          </Link>

          {/* Categories mega-menu - Desktop */}
          <div className="hidden lg:block relative">
            <Button 
              variant="ghost" 
              className={`flex items-center gap-1 text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 ${showCategories ? 'bg-emerald-50 text-emerald-700' : ''}`}
              onClick={() => setShowCategories(!showCategories)}
              data-testid="desktop-categories-btn"
            >
              <Menu className="h-4 w-4" />
              <span>{t('nav.categories')}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
            </Button>
            
            {showCategories && <CategoryMegaMenu counts={categoryCounts} onNavigate={() => setShowCategories(false)} />}
          </div>

          {/* Search bar (hidden on mobile on the search page, which has its own) */}
          <SearchAutocomplete className={`flex-1 max-w-2xl ${location.pathname.startsWith('/search') ? 'hidden md:block' : ''}`} />

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Link to="/favorites">
                  <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-emerald-600 hover:bg-emerald-50">
                    <Heart className="h-5 w-5" />
                  </Button>
                </Link>
                
                <Link to="/messages">
                  <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-emerald-600 hover:bg-emerald-50">
                    <MessageCircle className="h-5 w-5" />
                    {/* Only show badge when we confirmed there are unread messages (not null/loading, not 0) */}
                    {typeof unreadMessages === 'number' && unreadMessages > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                        {unreadMessages}
                      </Badge>
                    )}
                  </Button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 hover:bg-emerald-50">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-600">
                          {user.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden lg:block text-gray-700">{user.name?.split(' ')[0]}</span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="h-4 w-4" />
                        <span>{t('header.profile')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-listings" className="flex items-center gap-2 cursor-pointer">
                        <Package className="h-4 w-4" />
                        <span>{t('header.myListings')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/saved-searches" className="flex items-center gap-2 cursor-pointer" data-testid="header-saved-searches-link">
                        <BellRing className="h-4 w-4" />
                        <span>{t('header.savedSearches')}</span>
                        {savedSearchNew > 0 && <Badge className="ml-auto h-5 min-w-5 px-1.5 bg-emerald-600 text-[10px]" data-testid="header-saved-searches-badge">{savedSearchNew}</Badge>}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-purple-600">
                            <Shield className="h-4 w-4" />
                            <span>Admin Dashboard</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" />
                      <span>{t('header.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Link to="/create-listing">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 gap-2 shadow-md hover:shadow-lg transition-all">
                    <Plus className="h-4 w-4" />
                    {t('header.sell')}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-700 hover:text-emerald-600 hover:bg-emerald-50">
                    {t('header.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                    {t('header.signup')}
                  </Button>
                </Link>
                <Link to="/create-listing">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 gap-2">
                    <Plus className="h-4 w-4" />
                    {t('header.sell')}
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu */}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={handleMobileMenuOpenChange}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[88vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu">
                <SheetTitle className="sr-only">{t('nav.allCategories')}</SheetTitle>
                <div className="px-4 pt-4 pb-3 border-b flex items-center gap-3">
                  {user ? (
                    <Link to="/profile" onClick={handleMobileNavClick} className="flex items-center gap-3 flex-1 min-w-0" data-testid="mobile-menu-profile">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{t('nav.myProfile')}</p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex-1 flex gap-2">
                      <Link to="/login" onClick={handleMobileNavClick} className="flex-1" data-testid="mobile-menu-login">
                        <Button variant="outline" className="w-full h-10">{t('nav.login')}</Button>
                      </Link>
                      <Link to="/register" onClick={handleMobileNavClick} className="flex-1" data-testid="mobile-menu-signup">
                        <Button className="w-full h-10 bg-emerald-600 hover:bg-emerald-700">{t('nav.signup')}</Button>
                      </Link>
                    </div>
                  )}
                </div>

                {user && (
                  <div className="grid grid-cols-4 gap-1 px-3 py-2 border-b" data-testid="mobile-menu-quick-links">
                    {[
                      { to: '/my-listings', icon: Package, label: t('nav.myListings') },
                      { to: '/favorites', icon: Heart, label: t('nav.favorites') },
                      { to: '/saved-searches', icon: BellRing, label: t('header.savedSearches'), badge: savedSearchNew, green: true },
                      { to: '/messages', icon: MessageCircle, label: t('nav.messages'), badge: unreadMessages },
                      isAdmin ? { to: '/admin', icon: Shield, label: t('nav.admin'), admin: true } : null,
                    ].filter(Boolean).map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link key={item.to} to={item.to} onClick={handleMobileNavClick} data-testid={`mobile-quick-link-${item.to.replace('/', '')}`} className={`relative flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-gray-50 text-[11px] ${item.admin ? 'text-purple-600' : 'text-gray-700'}`}>
                          <Icon className="h-5 w-5" />
                          <span className="truncate max-w-full">{item.label}</span>
                          {typeof item.badge === 'number' && item.badge > 0 && (
                            <Badge className={`absolute top-0.5 right-2 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] ${item.green ? 'bg-emerald-600' : 'bg-red-500'}`}>{item.badge}</Badge>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}

                <div className="flex-1 overflow-y-auto px-3 py-3">
                  <CategoryBrowser counts={categoryCounts} onNavigate={handleMobileNavClick} />
                </div>

                <div className="p-3 border-t space-y-2 bg-white">
                  <Link to="/create-listing" onClick={handleMobileNavClick} className="block">
                    <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="mobile-menu-sell">
                      <Plus className="h-4 w-4" />
                      {t('nav.sell') || 'Sell an item'}
                    </Button>
                  </Link>
                  {user && (
                    <button type="button" className="w-full text-xs text-gray-500 hover:text-red-600 flex items-center justify-center gap-1 py-1" onClick={() => { logout(); setMobileMenuOpen(false); }} data-testid="mobile-menu-logout">
                      <LogOut className="h-3.5 w-3.5" />
                      {t('nav.logout') || 'Log out'}
                    </button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Click outside to close categories */}
      {showCategories && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowCategories(false)}
        />
      )}
    </header>
  );
};

export default Header;
